import {
  FuzzySearchConfig,
  RRFConfig,
  SearchableItemPhase,
  SemanticSearchConfig,
  UniversalSearchConfig,
  UniversalSearchMode,
} from './types'

// Re-export the config type for consumers who import from constants
export type { UniversalSearchConfig } from './types'

/**
 * Default RRF k constant.
 * Industry standard value (k=60) from the original RRF paper.
 * Only change with careful consideration and thorough testing.
 */
export const DEFAULT_RRF_K = 60

/** Default RRF threshold for filtering low-confidence results */
export const DEFAULT_RRF_THRESHOLD = 0.0143

/** Default semantic API timeout in milliseconds */
export const DEFAULT_SEMANTIC_TIMEOUT_MS = 10000

/** All valid phases for searchable items */
export const ALL_SEARCHABLE_ITEM_PHASES: SearchableItemPhase[] = ['P0', 'P1']

/** Minimum query length for search */
export const DEFAULT_SEARCH_MIN_QUERY_LENGTH = 3

/**
 * Common stop words to filter out from search queries.
 * These words are so common that they add noise to search results.
 */
export const DEFAULT_SEARCH_QUERY_STOP_WORDS = new Set([
  // Articles
  'a',
  'an',
  'the',
  // Prepositions
  'in',
  'on',
  'at',
  'to',
  'for',
  'of',
  'with',
  'by',
  'from',
  'as',
  'into',
  'through',
  'during',
  'before',
  'after',
  // Conjunctions
  'and',
  'or',
  'but',
  'nor',
  'so',
  'yet',
  // Pronouns
  'i',
  'me',
  'my',
  'myself',
  'we',
  'our',
  'ours',
  'ourselves',
  'you',
  'your',
  'yours',
  'yourself',
  'yourselves',
  'he',
  'him',
  'his',
  'himself',
  'she',
  'her',
  'hers',
  'herself',
  'it',
  'its',
  'itself',
  'they',
  'them',
  'their',
  'theirs',
  'themselves',
  'what',
  'which',
  'who',
  'whom',
  'this',
  'that',
  'these',
  'those',
  // Common verbs
  'am',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'have',
  'has',
  'had',
  'having',
  'do',
  'does',
  'did',
  'doing',
  'would',
  'should',
  'could',
  'ought',
  'cannot',
  'can',
  'will',
  'shall',
  'might',
  'must',
  // Common adverbs
  'just',
  'very',
  'really',
  'quite',
  'rather',
  'too',
  'also',
  'only',
  'even',
  'still',
  'already',
  'always',
  'never',
  // Other common words
  'about',
  'get',
  'got',
  'getting',
  'want',
  'need',
  'like',
  'some',
  'any',
  'no',
  'not',
  'more',
  'most',
  'other',
  'such',
  'own',
  'same',
  'than',
  'then',
  'now',
  'here',
  'there',
  'when',
  'where',
  'why',
  'how',
  'all',
  'each',
  'few',
  'both',
  'any',
  'most',
  'other',
  'some',
  'such',
  'only',
  'over',
  'because',
  'if',
  'while',
  // Contractions (common forms)
  "i'm",
  "i've",
  "i'll",
  "i'd",
  "you're",
  "you've",
  "you'll",
  "you'd",
  "he's",
  "he'll",
  "he'd",
  "she's",
  "she'll",
  "she'd",
  "it's",
  "it'll",
  "we're",
  "we've",
  "we'll",
  "we'd",
  "they're",
  "they've",
  "they'll",
  "they'd",
  "isn't",
  "aren't",
  "wasn't",
  "weren't",
  "hasn't",
  "haven't",
  "hadn't",
  "doesn't",
  "don't",
  "didn't",
  "won't",
  "wouldn't",
  "shan't",
  "shouldn't",
  "can't",
  "couldn't",
  "mustn't",
  "let's",
  "that's",
  "who's",
  "what's",
  "here's",
  "there's",
  "when's",
  "where's",
  "why's",
  "how's",
  'trouble',
  'help',
])

/** Default fuzzy search configuration */
export const DEFAULT_FUZZY_CONFIG: FuzzySearchConfig = {
  standardThreshold: 0.249,
  strictThreshold: 0.15,
  displayNameWeight: 3,
  keywordsWeight: 1,
}

/** Default semantic search configuration */
export const DEFAULT_SEMANTIC_CONFIG: SemanticSearchConfig = {
  similarityThreshold: 0.5,
  strictSimilarityThreshold: 0.85,
  similarityBucketSize: 0.05,
  keywordBoostThreshold: 0.5,
  minimumWordLength: 2,
  ignoreStopWords: true,
  additionalStopWords: [...DEFAULT_SEARCH_QUERY_STOP_WORDS],
  requireKeywordMatch: false,
  skipRanking: false,
  includeDisplaynameMatches: false,
  timeoutMs: DEFAULT_SEMANTIC_TIMEOUT_MS,
  embeddingBackend: 'sagemaker',
  embeddingModel: 'mxbai-embed-endpoint-1762835444',
}

/** Default penalty for fuzzy-only results (10% penalty) */
export const DEFAULT_FUZZY_ONLY_PENALTY = 0.9

/** Default RRF configuration */
export const DEFAULT_RRF_CONFIG: RRFConfig = {
  k: DEFAULT_RRF_K,
  threshold: DEFAULT_RRF_THRESHOLD,
  fuzzyOnlyPenalty: DEFAULT_FUZZY_ONLY_PENALTY,
}

/** Default universal search configuration */
export const DEFAULT_UNIVERSAL_SEARCH_CONFIG: UniversalSearchConfig = {
  mode: UniversalSearchMode.FUZZY,
  fuzzy: DEFAULT_FUZZY_CONFIG,
  semantic: DEFAULT_SEMANTIC_CONFIG,
  rrf: DEFAULT_RRF_CONFIG,
  minQueryLength: DEFAULT_SEARCH_MIN_QUERY_LENGTH,
  debounceMs: 500,
  scoreDiffThreshold: 0,
  maxResults: 10,
  enabledPhases: ['P0', 'P1'],
}
